import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { pagesKey } from '@dashboard/lib/queryKeys';
import DialogOld from './DialogOld';
import Form from './Formik/Form';
import { fetcher } from '@dashboard/services/request';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { isEmpty, debounce } from 'lodash';
import generateSlug from '../lib/generateSlug';
import Spinner from './Spinner';
import getSellerDomain from '../services/getSellerDomain';
import removeProtocolFromUrl from '../lib/removeProtocolFromUrl';
import logException from '../services/logException';

const DuplicatePageDialog = ({ isOpen, onClose, page, title }) => {
  const { sellerId, token, isAdmin, userSellerId, seller } = useAuth();
  const queryClient = useQueryClient();
  const history = useHistory();

  const domain = removeProtocolFromUrl(getSellerDomain(seller));

  const defaultPageTitle = (() => {
    switch (page.type) {
      case 'checkout': {
        return page.productTitle;
      }
      case 'event':
      case 'form': {
        return page.title;
      }
      default: {
        logException({ message: `Found page with invalid type ${page._id} ${page.type}` });
        return '';
      }
    }
  })();

  const [manualEdit, setManualEdit] = useState(false);
  const [isCheckingSlugAvailability, setIsCheckingSlugAvailability] = useState(true);

  useEffect(() => {
    if (isOpen === false) {
      setIsCheckingSlugAvailability(true);
    }
  }, [isOpen]);

  const checkSlugAvailability = useCallback(
    debounce(async ({ setFieldValue, slug }) => {
      /**
       * Avoid validating empty slugs, the response will be strange like `-2`. Its better
       * to allow server side slug creation if the page title is empty.
       */
      if (slug === '') {
        setFieldValue('slug', slug);
        setIsCheckingSlugAvailability(false);
        return;
      }

      setIsCheckingSlugAvailability(true);
      const params = new URLSearchParams({
        slug,
      });

      try {
        const response = await fetcher(
          `/sellers/${sellerId}/pages/${page._id}/check-slug-availability?${params.toString()}`,
          token,
          {
            method: 'GET',
          },
        );

        if (response.slug) {
          setFieldValue('slug', response.slug);
        }
      } catch (error) {
        logException({ message: 'Error checking slug availability', error });
      }

      setIsCheckingSlugAvailability(false);
    }, 300),
    [],
  );

  const copyPageMutation = useMutation({
    mutationFn: async values => {
      return fetcher(`/sellers/${sellerId}/pages/${page._id}/copy`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      });
    },
    onSuccess: data => {
      history.push(`/pages/${data._id}/customize`);
      queryClient.invalidateQueries({ queryKey: pagesKey });
    },
  });

  const initialValues = (() => {
    switch (page.type) {
      case 'checkout': {
        return {
          name: `${page.name} (copy)`,
          productTitle: defaultPageTitle,
          slug: '',
          duplicateIntoAdmin: false,
        };
      }
      case 'event':
      case 'form': {
        return {
          name: `${page.name} (copy)`,
          title: defaultPageTitle,
          slug: '',
          duplicateIntoAdmin: false,
        };
      }
      default: {
        return {
          name: `${page.name} (copy)`,
          slug: '',
          duplicateIntoAdmin: false,
        };
      }
    }
  })();

  return (
    <DialogOld isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="h-full flex-1 flex flex-col mt-2">
        <Form
          showState={false}
          initialValues={initialValues}
          onSubmit={async (values, actions) => {
            try {
              const params = (() => {
                switch (page.type) {
                  case 'checkout': {
                    return {
                      slug: values.slug,
                      name: values.name,
                      productTitle: values.productTitle,
                      sellerId: values.duplicateIntoAdmin ? userSellerId : sellerId,
                    };
                  }
                  case 'event':
                  case 'form': {
                    return {
                      slug: values.slug,
                      name: values.name,
                      title: values.title,
                      sellerId: values.duplicateIntoAdmin ? userSellerId : sellerId,
                    };
                  }
                  default: {
                    logException({
                      message: `Found page with invalid type ${page._id} ${page.type}`,
                    });
                    return {
                      slug: values.slug,
                      name: values.name,
                      sellerId: values.duplicateIntoAdmin ? userSellerId : sellerId,
                    };
                  }
                }
              })();

              const response = await copyPageMutation.mutateAsync(params);

              actions.resetForm();

              return {
                data: {
                  slug: response.data.slug,
                  name: response.data.name,
                  ...(page.type === 'checkout' && {
                    productTitle: response.data.productTitle,
                  }),
                  ...(page.type === 'form' && {
                    title: response.data.title,
                  }),
                  ...(page.type === 'event' && {
                    title: response.data.title,
                  }),
                },
              };
            } catch (err) {
              const { status, errors } = transformApiErrorToFormik(err);

              if (!isEmpty(errors)) {
                actions.setErrors(errors);
                return;
              } else {
                actions.setStatus(status);
                return;
              }
            }
          }}
          fields={({ setFieldValue, values }) => {
            useEffect(() => {
              if (isOpen) {
                const slug = generateSlug({ value: defaultPageTitle });
                checkSlugAvailability({ setFieldValue, slug });
              }
            }, [isOpen]);

            const pageTitleField = (() => {
              const onChangeHandler = (e, fieldName) => {
                if (e == null || e.target == null) return;

                const value = e.target.value ?? '';
                setFieldValue(fieldName, value);

                if (manualEdit === false) {
                  const newSlug = generateSlug({ value });
                  setFieldValue('slug', newSlug);
                  checkSlugAvailability({ setFieldValue, slug: newSlug });
                }
              };

              switch (page.type) {
                case 'checkout': {
                  return {
                    name: 'productTitle',
                    label: 'Product title',
                    type: 'text',
                    placeholder: 'Product title',
                    required: true,
                    onChange: e => onChangeHandler(e, 'productTitle'),
                  };
                }
                case 'event': {
                  return {
                    name: 'title',
                    label: 'Event name',
                    type: 'text',
                    placeholder: 'Event name',
                    required: true,
                    onChange: e => onChangeHandler(e, 'title'),
                  };
                }
                case 'form': {
                  return {
                    name: 'title',
                    label: 'Title',
                    type: 'text',
                    placeholder: 'Title',
                    optional: true,
                    onChange: e => onChangeHandler(e, 'title'),
                  };
                }
              }
            })();

            return [
              {
                name: 'name',
                label: 'Page name',
                tags: ['only visible to you'],
                type: 'text',
                placeholder: 'Page name',
                required: true,
              },
              pageTitleField,
              {
                name: 'slug',
                type: 'text',
                label: 'Page slug',
                hidePlaceholder: isCheckingSlugAvailability || values.slug !== '',
                disabled: isCheckingSlugAvailability,
                isRelative: true,
                onChange: e => {
                  if (e == null || e.target == null) return;
                  const value = e.target.value ?? '';
                  setManualEdit(true);
                  setFieldValue('slug', generateSlug({ value }));
                },
                required: false,
                additionalContent: (
                  <div>
                    <div className="text-sm mt-1 text-gray-400 break-all">{`${domain}/${values.slug}`}</div>
                    {isCheckingSlugAvailability && (
                      <div className="absolute top-8 left-0 pl-3 flex items-center pointer-events-none">
                        <Spinner size="small" />
                      </div>
                    )}
                  </div>
                ),
              },
              ...(isAdmin
                ? [
                    {
                      name: 'duplicateIntoAdmin',
                      label: 'Duplicate into admin account',
                      type: 'checkbox',
                      required: false,
                    },
                  ]
                : []),
            ];
          }}
          submitButton={{
            label: 'Continue',
            size: 'full',
            arrowRight: true,
            disabled: isCheckingSlugAvailability,
          }}
        ></Form>
      </div>
    </DialogOld>
  );
};

export default DuplicatePageDialog;
